import { getData } from "country-list"
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react"
import { useMemo, useState } from "react"

import {
  Button,
  cn,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Field,
  FieldError,
  FieldLabel,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@atlant/ui"

import { toCountryCode } from "#utils/country.ts"

const countries = getData()

interface FormCountrySelectProps {
  value: string
  onChange: (value: string) => void
  name?: string
  label?: string
  placeholder?: string
  disabled?: boolean
  className?: string
  error?: string
}

export const FormCountrySelect = ({
  value,
  onChange,
  name,
  label = "Country",
  placeholder = "Select country",
  disabled,
  className,
  error,
}: FormCountrySelectProps) => {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")

  const filtered = useMemo(() => {
    if (!search) return countries
    const term = search.toLowerCase()
    return countries.filter(
      (c) => c.name.toLowerCase().includes(term) || c.code.toLowerCase().includes(term),
    )
  }, [search])

  const code = toCountryCode(value)
  const selected = countries.find((c) => c.code === code)
  const id = name ?? label?.toLowerCase().replace(/\s+/g, "-")

  return (
    <Field className={className} data-invalid={error ? true : undefined}>
      {label && (
        <FieldLabel htmlFor={id} className="text-muted-foreground text-sm font-medium">
          {label}
        </FieldLabel>
      )}
      <Popover modal open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            type="button"
            aria-invalid={error ? true : undefined}
            className={cn(
              "shadow-custom! border-input hover:bg-input/10 h-10 w-full justify-between gap-2" +
                " truncate bg-white px-3 text-left font-normal dark:shadow-none!",
              !value && "text-muted-foreground",
              error && "border-destructive dark:border-destructive",
            )}
          >
            <span className="truncate">
              {selected ? (
                selected.name
              ) : (
                <span className="text-placeholder">{placeholder}</span>
              )}
            </span>
            <ChevronsUpDownIcon
              size={16}
              className="text-muted-foreground/80 shrink-0"
              aria-hidden="true"
            />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="border-input w-(--radix-popper-anchor-width) p-1"
          align="start"
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search country..."
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {filtered.map((country) => (
                  <CommandItem
                    key={country.code}
                    value={country.code}
                    onSelect={() => {
                      onChange(country.code)
                      setOpen(false)
                      setSearch("")
                    }}
                  >
                    <span className="flex-1">{country.name}</span>
                    {code === country.code && <CheckIcon size={16} className="ml-auto" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {error && <FieldError>{error}</FieldError>}
    </Field>
  )
}
